/**
 * RightDetailsPanel Component
 *
 * Displays detailed information about the selected person.
 * Shows profile, timeline events, and linked records.
 * Supports editing person details and deleting persons.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { updatePerson, deletePerson } from '@/api/tree-dashboard.api';
import { uploadPersonImage } from '@/api/storage.api';
import type { PersonNode, TimelineEvent, LinkedRecord } from '../types';
import { validateImageFile, safeValidate, personUpdateSchema, getFirstErrorMessage } from '@/lib/validation';

interface RightDetailsPanelProps {
  person: PersonNode | null;
  timelineEvents: TimelineEvent[];
  linkedRecords: LinkedRecord[];
  isLoading?: boolean;
  onClose?: () => void;
  onPersonUpdated?: () => void;
  onPersonDeleted?: () => void;
}

/**
 * Format location string
 */
function formatLocation(birthPlace?: string | null, deathPlace?: string | null): string {
  if (birthPlace && deathPlace && birthPlace === deathPlace) {
    return birthPlace;
  }
  return birthPlace || deathPlace || '';
}

/**
 * Format life dates with location
 */
function formatLifeDates(person: PersonNode, t: (key: string) => string): string {
  const birthYear = person.birth_date ? new Date(person.birth_date).getFullYear() : '?';

  // Check for sentinel date (deceased but date unknown)
  let deathYear = t('tree.person.present');
  if (person.death_date) {
    const year = new Date(person.death_date).getFullYear();
    deathYear = year === 1 ? '?' : year.toString();
  }

  const location = formatLocation(person.birth_place, person.death_place);

  if (!person.birth_date && !person.death_date) {
    return location || t('tree.detailsPanel.lifeDetailsNotIncluded');
  }

  return location ? `${birthYear} — ${deathYear} • ${location}` : `${birthYear} — ${deathYear}`;
}

/**
 * Get icon for record type
 */
function getRecordIcon(type: LinkedRecord['record_type']): string {
  const iconMap: Record<LinkedRecord['record_type'], string> = {
    birth_certificate: 'description',
    service_record: 'military_tech',
    marriage_certificate: 'favorite',
    photo: 'photo',
    document: 'description',
    other: 'folder',
  };
  return iconMap[type] || 'folder';
}

export function RightDetailsPanel({
  person,
  timelineEvents,
  linkedRecords,
  isLoading = false,
  onClose,
  onPersonUpdated,
  onPersonDeleted,
}: RightDetailsPanelProps) {
  const { t } = useTranslation('common');

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Living status state (separate from date_of_death to control visibility)
  const [isLiving, setIsLiving] = useState<boolean>(true);

  // Form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    nickname: '',
    gender: 'other' as 'male' | 'female' | 'other',
    date_of_birth: '',
    date_of_death: '',
    marital_status: null as 'single' | 'married' | 'divorced' | 'widowed' | null,
    notes: '',
    profile_image: null as File | null,
  });

  // Reset form when person changes
  useEffect(() => {
    if (person) {
      // Check if death date is the sentinel value (unknown date)
      const isSentinelDate = person.death_date && new Date(person.death_date).getFullYear() === 1;
      const deathDate = isSentinelDate ? '' : (person.death_date || '');

      setEditForm({
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        nickname: person.nickname || '',
        gender: person.gender || 'other',
        date_of_birth: person.birth_date || '',
        date_of_death: deathDate,
        marital_status: person.marital_status || null,
        notes: person.notes || '',
        profile_image: null,
      });
      setIsEditMode(false);
      setImagePreview(null);
      setIsLiving(!person.death_date); // Set living status based on death date
    }
  }, [person?.id]);

  // Form handlers
  const handleInputChange = (field: string, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Use centralized image validation
      const validationResult = validateImageFile(file, 5);
      if (!validationResult.valid) {
        toast.error(validationResult.error);
        return;
      }

      setEditForm(prev => ({ ...prev, profile_image: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save handler
  const handleSave = async () => {
    if (!person) return;

    // Use centralized validation schema
    const validationResult = safeValidate(personUpdateSchema, editForm);
    if (!validationResult.success) {
      const errorMessage = getFirstErrorMessage(validationResult.errors);
      toast.error(errorMessage);
      return;
    }

    setIsSaving(true);

    try {
      // Step 1: Upload image if new one selected
      let profileImageUrl = person.profile_image_url;
      if (editForm.profile_image) {
        toast.info(t('tree.toasts.uploadingImage'));
        profileImageUrl = await uploadPersonImage(person.id, editForm.profile_image);
      }

      // Step 2: Prepare death date (use sentinel for deceased without date)
      let deathDate = editForm.date_of_death || null;
      if (!isLiving && !deathDate) {
        // Person is deceased but no death date provided - use sentinel value
        deathDate = '0001-01-01';
      }

      // Step 3: Update person data
      await updatePerson(person.id, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim() || null,
        nickname: editForm.nickname.trim() || null,
        gender: editForm.gender,
        date_of_birth: editForm.date_of_birth || null,
        date_of_death: deathDate,
        marital_status: editForm.marital_status,
        notes: editForm.notes.trim() || null,
        profile_image_url: profileImageUrl,
      });

      toast.success(t('tree.toasts.personUpdated'));
      setIsEditMode(false);
      setImagePreview(null);

      // Trigger refresh in parent component
      if (onPersonUpdated) {
        onPersonUpdated();
      }
    } catch (error: any) {
      console.error('Failed to update person:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      const errorMessage = error?.message || error?.details?.message || t('tree.errors.updateFailed');
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!person) return;

    setIsDeleting(true);

    try {
      await deletePerson(person.id);
      toast.success(t('tree.toasts.personDeleted'));
      setShowDeleteConfirm(false);

      // Close panel and trigger refresh
      if (onClose) onClose();
      if (onPersonDeleted) onPersonDeleted();
    } catch (error) {
      console.error('Failed to delete person:', error);
      toast.error(t('tree.errors.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <aside className="w-80 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-background flex flex-col items-center justify-center h-full z-40 shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.02)]">
        <div className="text-center px-6">
          <div className="size-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('tree.detailsPanel.loading')}
          </p>
        </div>
      </aside>
    );
  }

  // Empty state
  if (!person) {
    return (
      <aside className="w-80 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-background flex flex-col items-center justify-center h-full z-40 shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.02)]">
        <div className="text-center px-6">
          <div className="size-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-3xl text-slate-400">person</span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('tree.detailsPanel.selectPerson')}
          </p>
        </div>
      </aside>
    );
  }

  const fullName = `${person.first_name} ${person.last_name || ''}`.trim();
  const lifeDates = formatLifeDates(person, t);

  return (
    <aside className="w-full md:w-80 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-background flex flex-col h-full z-40 shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.02)] md:shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.02)]">
      {/* Header */}
      <div className="p-4 md:p-6 overflow-y-auto custom-scrollbar flex-1">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">
            {t('tree.detailsPanel.title')}
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="size-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
              aria-label="Close panel"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          )}
        </div>

        {/* Profile Section */}
        <div className="flex flex-col gap-4 mb-8">
          {/* Profile Picture - Full Width Rectangle */}
          <div className="w-full h-52 rounded-xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border border-slate-200 dark:border-slate-700">
            {person.profile_image_url ? (
              <img
                src={person.profile_image_url}
                alt={fullName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600">
                    {person.gender === 'male' ? 'man' : person.gender === 'female' ? 'woman' : 'person'}
                  </span>
                  <p className="text-xs text-slate-400 dark:text-slate-500">{t('tree.detailsPanel.noPhoto')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Name and Nickname */}
          <div className="text-center">
            <h2 className="text-xl font-bold">{fullName}</h2>
            {person.nickname && (
              <p className="text-sm text-slate-500 dark:text-slate-400 italic mt-1">
                "{person.nickname}"
              </p>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{lifeDates}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 w-full">
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className="flex-1 py-2 bg-primary/10 text-primary rounded-lg text-xs font-bold hover:bg-primary hover:text-white transition-all"
            >
              {isEditMode ? t('tree.detailsPanel.cancelEdit') : t('tree.detailsPanel.editProfile')}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isEditMode}
              className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete person"
            >
              <span className="material-symbols-outlined text-lg">delete</span>
            </button>
          </div>
        </div>

        {/* Personal Details or Edit Form */}
        {!isEditMode ? (
          /* VIEW MODE - Personal Details */
          <div className="space-y-4 mb-8">
            <p className="text-[10px] font-bold text-slate-400 uppercase">{t('tree.detailsPanel.personalDetails')}</p>

            {/* Birth Date */}
            {person.birth_date && (
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-slate-400 text-lg">cake</span>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('tree.detailsPanel.dateOfBirth')}</p>
                  <p className="text-sm font-semibold">
                    {new Date(person.birth_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Status (Living/Deceased) */}
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-slate-400 text-lg">
                {person.death_date ? 'mood_bad' : 'favorite'}
              </span>
              <div className="flex-1">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('tree.detailsPanel.status')}</p>
                {person.death_date ? (
                  <>
                    <p className="text-sm font-semibold">{t('tree.detailsPanel.deceased')}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(person.death_date).getFullYear() === 1
                        ? t('tree.detailsPanel.dateUnknown')
                        : new Date(person.death_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                    </p>
                  </>
                ) : (
                  <p className="text-sm font-semibold text-green-600 dark:text-green-400">{t('tree.detailsPanel.living')}</p>
                )}
              </div>
            </div>

            {/* Age/Lifespan */}
            {person.age !== undefined && person.age !== null && (
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-slate-400 text-lg">schedule</span>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {person.death_date ? t('tree.detailsPanel.lifespan') : t('tree.detailsPanel.currentAge')}
                  </p>
                  <p className="text-sm font-semibold">{person.age} {t('tree.detailsPanel.years')}</p>
                </div>
              </div>
            )}

            {/* Gender */}
            {person.gender && (
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-slate-400 text-lg">wc</span>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('tree.addPersonModal.gender')}</p>
                  <p className="text-sm font-semibold capitalize">{t(`gender.${person.gender}`)}</p>
                </div>
              </div>
            )}

            {/* Marital Status */}
            {person.marital_status && (
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-slate-400 text-lg">favorite</span>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('tree.detailsPanel.maritalStatus')}</p>
                  <p className="text-sm font-semibold capitalize">{t(`tree.maritalStatus.${person.marital_status}`)}</p>
                </div>
              </div>
            )}

            {/* Notes */}
            {person.notes && (
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-slate-400 text-lg">note</span>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('tree.detailsPanel.notes')}</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {person.notes}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* EDIT MODE - Edit Form */
          <div className="space-y-5 mb-8">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-slate-400 uppercase">{t('tree.detailsPanel.editPersonDetails')}</p>
              {isSaving && (
                <div className="flex items-center gap-2 text-xs text-primary">
                  <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span>{t('tree.detailsPanel.saving')}</span>
                </div>
              )}
            </div>

            {/* Profile Image Upload */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.profileImage')}
              </label>
              <div className="flex items-start gap-3">
                {/* Image Preview */}
                <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 flex-shrink-0 bg-slate-50 dark:bg-slate-800">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : person.profile_image_url ? (
                    <img src={person.profile_image_url} alt="Current" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-2xl text-slate-300 dark:text-slate-600">
                        {editForm.gender === 'male' ? 'man' : editForm.gender === 'female' ? 'woman' : 'person'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md text-xs font-medium cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">upload</span>
                    {t('tree.addPersonModal.chooseImage')}
                  </label>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setEditForm(prev => ({ ...prev, profile_image: null }));
                      }}
                      className="ml-2 text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      {t('tree.addPersonModal.removeImage')}
                    </button>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t('tree.addPersonModal.imageConstraints')}
                  </p>
                </div>
              </div>
            </div>

            {/* First Name */}
            <div className="space-y-1.5">
              <label htmlFor="first_name" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.firstName')} <span className="text-red-500">*</span>
              </label>
              <input
                id="first_name"
                type="text"
                value={editForm.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.firstName')}
                required
              />
            </div>

            {/* Last Name */}
            <div className="space-y-1.5">
              <label htmlFor="last_name" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.lastName')}
              </label>
              <input
                id="last_name"
                type="text"
                value={editForm.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.lastName')}
              />
            </div>

            {/* Nickname */}
            <div className="space-y-1.5">
              <label htmlFor="nickname" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.nickname')}
              </label>
              <input
                id="nickname"
                type="text"
                value={editForm.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                placeholder={t('tree.addPersonModal.nicknamePlaceholder')}
              />
            </div>

            {/* Gender */}
            <div className="space-y-1.5">
              <label htmlFor="gender" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.gender')} <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                value={editForm.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              >
                <option value="male">{t('gender.male')}</option>
                <option value="female">{t('gender.female')}</option>
                <option value="other">{t('gender.other')}</option>
              </select>
            </div>

            {/* Date of Birth */}
            <div className="space-y-1.5">
              <label htmlFor="date_of_birth" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.addPersonModal.dateOfBirth')}
              </label>
              <input
                id="date_of_birth"
                type="date"
                value={editForm.date_of_birth}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>

            {/* Living Status Checkbox */}
            <div className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                id="is-living"
                checked={isLiving}
                onChange={(e) => {
                  setIsLiving(e.target.checked);
                  if (e.target.checked) {
                    // If marking as living, clear death date
                    handleInputChange('date_of_death', '');
                  }
                }}
                className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="is-living" className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                {t('tree.addPersonModal.personIsLiving')}
              </label>
            </div>

            {/* Date of Death (conditional) */}
            {!isLiving && (
              <div className="space-y-1.5">
                <label htmlFor="date_of_death" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  {t('tree.addPersonModal.dateOfDeath')}
                </label>
                <input
                  id="date_of_death"
                  type="date"
                  value={editForm.date_of_death}
                  onChange={(e) => handleInputChange('date_of_death', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            )}

            {/* Marital Status */}
            <div className="space-y-1.5">
              <label htmlFor="marital_status" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.detailsPanel.maritalStatus')}
              </label>
              <select
                id="marital_status"
                value={editForm.marital_status || ''}
                onChange={(e) => handleInputChange('marital_status', e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              >
                <option value="">{t('tree.maritalStatus.selectStatus')}</option>
                <option value="single">{t('tree.maritalStatus.single')}</option>
                <option value="married">{t('tree.maritalStatus.married')}</option>
                <option value="divorced">{t('tree.maritalStatus.divorced')}</option>
                <option value="widowed">{t('tree.maritalStatus.widowed')}</option>
              </select>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <label htmlFor="notes" className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {t('tree.detailsPanel.notes')}
              </label>
              <textarea
                id="notes"
                value={editForm.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                placeholder={t('tree.addPersonModal.notesPlaceholder')}
              />
            </div>

            {/* Save/Cancel Buttons */}
            <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setIsEditMode(false)}
                disabled={isSaving}
                className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !editForm.first_name.trim()}
                className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('tree.detailsPanel.saving')}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">save</span>
                    {t('actions.save')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Timeline Events */}
        {timelineEvents.length > 0 && (
          <div className="space-y-6 mb-8">
            <p className="text-[10px] font-bold text-slate-400 uppercase mb-3">
              {t('tree.detailsPanel.timelineEvents')}
            </p>
            <div className="space-y-4">
              {timelineEvents.map((event) => (
                <div key={event.id} className="flex gap-4">
                  <div className="w-12 text-[10px] font-bold text-primary pt-1">
                    {event.year}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{event.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linked Records */}
        {linkedRecords.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase mb-3">
              {t('tree.detailsPanel.linkedRecords')} ({linkedRecords.length})
            </p>
            <div className="grid grid-cols-2 gap-2">
              {linkedRecords.map((record) => (
                <button
                  key={record.id}
                  className="aspect-square rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center p-2 text-center group cursor-pointer hover:border-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-slate-400 group-hover:text-primary mb-1">
                    {getRecordIcon(record.record_type)}
                  </span>
                  <p className="text-[10px] font-semibold">{record.title}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action */}
      <div className="p-6 border-t border-slate-200 dark:border-slate-800">
        <button
          onClick={() => toast.info(t('tree.toasts.featureComingSoon'))}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-900 dark:bg-primary text-white rounded-xl text-sm font-bold shadow-lg shadow-primary/20 hover:bg-slate-800 dark:hover:bg-primary-hover transition-colors"
        >
          {t('tree.detailsPanel.viewTree')}
          <span className="material-symbols-outlined">account_tree</span>
        </button>
      </div>

      {/* Delete Confirmation Dialog (2-Stage) */}
      {showDeleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-6 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header with Icon */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-900/10 p-6 border-b border-red-200 dark:border-red-800">
              <div className="flex items-start gap-4">
                <div className="size-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0 ring-4 ring-red-50 dark:ring-red-900/20">
                  <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-2xl">
                    warning
                  </span>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                    {t('tree.deleteConfirmation.title')}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t('tree.deleteConfirmation.subtitle')}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Warning Message */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                    {t('tree.deleteConfirmation.warningMessage', { name: `${person?.first_name} ${person?.last_name}` })}
                  </p>
                </div>

                {/* Impact List */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                    {t('tree.deleteConfirmation.whatWillBeDeleted')}
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="material-symbols-outlined text-red-500 text-base mt-0.5">close</span>
                      <span>{t('tree.deleteConfirmation.personalInfo')}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="material-symbols-outlined text-red-500 text-base mt-0.5">close</span>
                      <span>{t('tree.deleteConfirmation.relationships')}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="material-symbols-outlined text-red-500 text-base mt-0.5">close</span>
                      <span>{t('tree.deleteConfirmation.attachments')}</span>
                    </li>
                  </ul>
                </div>

                {/* Confirmation Notice */}
                <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    <span className="material-symbols-outlined text-sm align-middle mr-1">info</span>
                    {t('tree.deleteConfirmation.notice')}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-4 flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 active:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('tree.deleteConfirmation.deleting')}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">delete_forever</span>
                    {t('tree.deleteConfirmation.deletePermanently')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </aside>
  );
}
